/**
 * Renderização do texto dos documentos legais.
 *
 * Vive em componente próprio porque DUAS telas mostram o mesmo texto: a leitura
 * pública (`/legal`) e o aceite obrigatório (`/aceite`). Duplicar o renderizador
 * significaria que uma correção de formatação valeria só em uma delas — e a que
 * ficaria para trás seria justamente a do aceite, onde o texto tem consequência
 * jurídica.
 *
 * Markdown mínimo, sem biblioteca: os documentos usam apenas cabeçalho,
 * parágrafo, lista, tabela, negrito e regra horizontal, e nada disso justifica
 * trazer um interpretador com sanitizador para o pacote da aplicação. O conteúdo
 * vem do NOSSO servidor, de um arquivo do repositório — não é entrada de
 * usuário, e mesmo assim nada aqui usa `dangerouslySetInnerHTML`.
 */

/** `**assim**` vira negrito. É a única marcação inline que os documentos usam. */
export function Negrito({ texto }: { texto: string }): JSX.Element {
  return (
    <>
      {texto.split(/(\*\*[^*]+\*\*)/g).map((parte, i) =>
        parte.startsWith('**') && parte.endsWith('**') ? (
          <strong key={i} className="font-semibold text-ink">
            {parte.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{parte}</span>
        ),
      )}
    </>
  );
}

export function TextoLegal({ markdown }: { markdown: string }): JSX.Element {
  const blocos = markdown.split('\n');
  const saida: JSX.Element[] = [];
  let tabela: string[] = [];

  const fecharTabela = (chave: string): void => {
    if (tabela.length === 0) return;
    const linhas = tabela
      .filter((l) => !/^\|[\s|:-]+\|$/.test(l))
      .map((l) => l.split('|').slice(1, -1).map((c) => c.trim()));
    const [cabecalho, ...corpo] = linhas;
    saida.push(
      <div key={chave} className="my-3 overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {(cabecalho ?? []).map((c, i) => (
                <th key={i} className="border-b border-line py-1.5 pr-4 text-left font-semibold text-ink">
                  <Negrito texto={c} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {corpo.map((linha, i) => (
              <tr key={i}>
                {linha.map((c, j) => (
                  <td key={j} className="border-b border-line py-1.5 pr-4 align-top text-ink-muted">
                    <Negrito texto={c} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>,
    );
    tabela = [];
  };

  blocos.forEach((linha, i) => {
    const chave = `l${i}`;
    if (linha.startsWith('|')) {
      tabela.push(linha);
      return;
    }
    fecharTabela(`t${i}`);

    if (linha.trim() === '') return;
    if (linha.startsWith('---')) {
      saida.push(<hr key={chave} className="my-5 border-line" />);
    } else if (linha.startsWith('## ')) {
      saida.push(
        <h2 key={chave} className="mt-6 text-base font-semibold text-ink">
          <Negrito texto={linha.slice(3)} />
        </h2>,
      );
    } else if (linha.startsWith('- ')) {
      saida.push(
        <li key={chave} className="ml-4 list-disc text-sm leading-relaxed text-ink-muted">
          <Negrito texto={linha.slice(2)} />
        </li>,
      );
    } else {
      saida.push(
        <p key={chave} className="mt-2 text-sm leading-relaxed text-ink-muted">
          <Negrito texto={linha} />
        </p>,
      );
    }
  });
  fecharTabela('tf');
  return <>{saida}</>;
}
